import { useEffect, useMemo, useState } from 'react';
import { Building2, Check, ChevronDown } from 'lucide-react';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useAgentContext } from '@Providers';

import { Popover, PopoverContent, PopoverTrigger } from '@Components/ui-v2/popover';
import { useAgents } from '@CustomHooks';
import { AgentEntry } from '@Models';

const formatName = (sourceName: string | undefined): string => {
  if (!sourceName) return 'Global Agent';
  return sourceName.charAt(0).toUpperCase() + sourceName.slice(1).toLowerCase();
};

const ConnectorPicker = () => {
  const { t } = useTranslation();
  const { data: agents } = useAgents();
  const { selectedAgent, setSelectedAgent, selectAgent } = useAgentContext();
  const [open, setOpen] = useState(false);

  const connectors = useMemo<AgentEntry[]>(() => {
    if (!agents) return [];
    const seen = new Map<string, AgentEntry>();
    agents.forEach((agent) => {
      const key = agent.source_name || 'global';
      if (!seen.has(key)) seen.set(key, agent);
    });
    return Array.from(seen.values());
  }, [agents]);

  useEffect(() => {
    if (connectors.length === 0) return;
    const current = connectors.find((c) => c.source_name === selectedAgent?.source_name);
    if (!current) setSelectedAgent(connectors[0]);
  }, [connectors, selectedAgent, setSelectedAgent]);

  if (connectors.length <= 1) return null;

  const label = formatName(selectedAgent?.source_name);

  const handlePick = (agent: AgentEntry) => {
    selectAgent(agent);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-full bg-transparent px-3 py-1.5 text-sm font-medium text-gray-700 cursor-pointer transition-colors duration-150 hover:bg-gray-100 focus:outline-none">
          <Building2 size={14} strokeWidth={1.75} className="text-gray-400" aria-hidden />
          <span>{label}</span>
          <motion.span
            animate={{ rotate: open ? 180 : 0 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            className="inline-flex items-center justify-center">
            <ChevronDown size={14} strokeWidth={1.75} className="text-gray-400" aria-hidden />
          </motion.span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="top"
        sideOffset={6}
        className="agent-root z-[120] min-w-[190px] w-auto overflow-hidden rounded-xl border border-gray-200 bg-[#fbfaf9] p-0 shadow-lg">
        <div className="px-3 pt-2.5 pb-1">
          <p className="text-center text-[10px] font-semibold uppercase tracking-wide text-gray-400">
            {t('Agent.ConnectorListTitle')}
          </p>
        </div>
        <div>
          {connectors.map((agent) => {
            const isActive = agent.source_name === selectedAgent?.source_name;
            return (
              <button
                key={agent.source_name || 'global'}
                type="button"
                onClick={() => handlePick(agent)}
                className={`flex w-full cursor-pointer items-center justify-between bg-transparent px-3 py-2 text-left transition-colors duration-100 hover:bg-[#f4f1ed] focus:outline-none ${
                  isActive ? 'text-[#7c6cf6]' : 'text-gray-700'
                }`}>
                <div className="flex min-w-0 items-center gap-2">
                  <Building2 size={14} strokeWidth={1.75} className="flex-shrink-0 text-gray-400" />
                  <div className="min-w-0">
                    <div className={`truncate text-sm ${isActive ? 'font-semibold' : 'font-medium'}`}>
                      {formatName(agent.source_name)}
                    </div>
                    {agent.language && <div className="truncate text-[10px] text-gray-400">{agent.language}</div>}
                  </div>
                </div>
                {isActive && <Check size={14} strokeWidth={1.75} className="ml-2 flex-shrink-0 text-[#7c6cf6]" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ConnectorPicker;
